import axios from 'axios';
import { RedactionType } from 'skyflow-js/types/utils/common';
import Skyflow from 'skyflow-js';
import {
  SkyflowInitPayload,
  SkyflowRecordsResponse,
  SkyflowRecordFields,
} from './types';
import { environment } from '../../environments/environment';

export interface IUpdatePatientPayload {
  address: string;
  address_2?: string;
  city: string;
  state: string;
  zip: string;
  dob: string;
  patientId: string;
}

class SkyflowClient {
  readClient: Skyflow | null;

  insertClient: Skyflow | null;

  updateClient: Skyflow | null;

  vaultId: string | null;

  vaultBaseUrl: string | null;

  vaultBasePrefix: string | null;

  vaultLambdaBaseUrl: string | null;

  authToken: string;

  RedactionType: typeof Skyflow.RedactionType;

  constructor({ authToken }: SkyflowInitPayload) {
    this.authToken = authToken;
    this.readClient = null;
    this.insertClient = null;
    this.updateClient = null;
    this.vaultId = null;
    this.vaultBaseUrl = null;
    this.vaultBasePrefix = null;
    this.vaultLambdaBaseUrl = null;
    this.RedactionType = Skyflow.RedactionType;
  }

  async init() {
    const config = await this.getConfig();
    if (!config) {
      throw new Error('Failed to fetch vault config');
    }
    this.vaultId = config.vaultId;
    this.vaultBaseUrl = config.vaultBaseUrl;
    this.vaultBasePrefix = config.basePrefix;
  }

  async getConfig() {
    try {
      if (environment.euVaultBaseUrl) {
        const response = await axios.get(
          `${environment.euVaultBaseUrl}/vault/config?vaultTable=${environment.vaultImsTable}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.status === 200) {
          this.vaultLambdaBaseUrl = environment.euVaultBaseUrl;
          return response.data;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    try {
      if (environment.usVaultBaseUrl) {
        const response = await axios.get(
          `${environment.usVaultBaseUrl}/vault/config?vaultTable=${environment.vaultImsTable}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.status === 200) {
          this.vaultLambdaBaseUrl = environment.usVaultBaseUrl;
          return response.data;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    return null;
  }

  async getBearerToken(actionType: string) {
    let route = '';
    switch (actionType) {
      case 'insert':
        route = 'insert-token';
        break;
      case 'detokenize':
        route = 'detokenize-token';
        break;
      case 'update':
        route = 'insert-token';
        break;
      default:
        throw new Error('Invalid token type');
    }

    const url = `${this.vaultLambdaBaseUrl}/vault/v1/${this.vaultBasePrefix}/${route}`;

    const response = await axios.get(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.authToken}`,
      },
    });
    const data = await response.data;
    return data.token;
  }

  async getSkyflowClient(actionType: string) {
    try {
      if (!this.vaultId || !this.vaultBaseUrl) {
        await this.init();
      }
      if (actionType === 'insert') {
        if (!this.insertClient) {
          this.insertClient = Skyflow.init({
            vaultID: this.vaultId as string,
            vaultURL: this.vaultBaseUrl as string,
            getBearerToken: () => this.getBearerToken(actionType),
            options: {
              logLevel: Skyflow.LogLevel.ERROR,
            },
          });
        }
        return this.insertClient;
      }
      if (actionType === 'update') {
        if (!this.updateClient) {
          this.updateClient = Skyflow.init({
            vaultID: this.vaultId as string,
            vaultURL: this.vaultBaseUrl as string,
            getBearerToken: () => this.getBearerToken(actionType),
            options: {
              logLevel: Skyflow.LogLevel.ERROR,
            },
          });
        }
        return this.updateClient;
      }

      if (actionType === 'detokenize') {
        if (!this.readClient) {
          this.readClient = Skyflow.init({
            vaultID: this.vaultId as string,
            vaultURL: this.vaultBaseUrl as string,
            getBearerToken: () => this.getBearerToken(actionType),
            options: {
              logLevel: Skyflow.LogLevel.ERROR,
            },
          });
        }
        return this.readClient;
      }
    
      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      return null;
    }
  }

  async update<T extends SkyflowRecordFields>(
    data: IUpdatePatientPayload | IUpdatePatientPayload[],
  ) {
    let records = data;
    if (!Array.isArray(data)) {
      records = [data];
    }
    const client = (await this.getSkyflowClient('update')) as Skyflow;
    const response = await client.insert(
      {
        records: (records as IUpdatePatientPayload[]).map(({
          patientId,
          ...record
        }) => ({
          table: environment.vaultImsTable as string,
          fields: {
            patient_id: patientId,
            ...record
          },
        })),
      },
      {
        tokens: true,
        upsert: [
          {
            table: environment.vaultImsTable as string,
            column: 'patient_id',
          }
        ]
      },
    );
    return response as SkyflowRecordsResponse<T>;
  }

  async get<T extends SkyflowRecordFields>(
    data: string[] | string,
    redaction: RedactionType = Skyflow.RedactionType.PLAIN_TEXT,
  ) {
    let records = data;
    if (!Array.isArray(data)) {
      records = [data];
    }
    const client = (await this.getSkyflowClient('detokenize')) as Skyflow;
    const response = await client.get({
      records: [
        {
          table: environment.vaultImsTable as string,
          ids: [...(records as string[])],
          redaction,
        },
      ],
    });
    return response as SkyflowRecordsResponse<T>;
  }
}

let client: SkyflowClient | null = null;

export const getSkyflowClient = ({
  authToken,
}: SkyflowInitPayload) => {
  if (
    !client ||
    client.authToken !== authToken
  ) {
    client = new SkyflowClient({ authToken });
    return client;
  }
  return client;
};
